import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { Alert, type AlertProps } from "../ui";
import { sharedCommonDe } from "./sharedCommonTranslations";
import { useI18nOptional } from "../i18n/useI18n";
import { spacing } from "../layout/spacing";

type DismissibleHintsContextValue = {
  storageKey: string;
};

const DismissibleHintsContext = createContext<DismissibleHintsContextValue | null>(null);

/**
 * Produktseitig konfigurierter Scope für ausblendbare Hinweise.
 * `storagePrefix` ist die produktspezifische LocalStorage-Konstante
 * (z. B. "@mqmarketing/dismissed-hints:"), `userId` kommt aus dem Produkt-Auth.
 */
export function DismissibleHintsProvider(props: {
  storagePrefix: string;
  userId: string;
  children: React.ReactNode;
}) {
  const value = useMemo(
    () => ({ storageKey: `${props.storagePrefix}${props.userId || "anonymous"}` }),
    [props.storagePrefix, props.userId]
  );
  return <DismissibleHintsContext.Provider value={value}>{props.children}</DismissibleHintsContext.Provider>;
}

export function useDismissibleHint(hintKey: string) {
  const ctx = useContext(DismissibleHintsContext);
  if (!ctx) throw new Error("useDismissibleHint must be used within DismissibleHintsProvider.");
  const storageKey = ctx.storageKey;

  // Start with true to avoid flashing before the localStorage query runs
  const [isDismissed, setIsDismissed] = useState<boolean>(true);
  const [isReady, setIsReady] = useState<boolean>(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      const dismissed = stored ? JSON.parse(stored) : [];
      setIsDismissed(dismissed.includes(hintKey));
    } catch {
      setIsDismissed(false);
    } finally {
      setIsReady(true);
    }
  }, [storageKey, hintKey]);

  const dismiss = () => {
    try {
      const stored = localStorage.getItem(storageKey);
      const dismissed = stored ? JSON.parse(stored) : [];
      if (!dismissed.includes(hintKey)) {
        const updated = [...dismissed, hintKey];
        localStorage.setItem(storageKey, JSON.stringify(updated));
        setIsDismissed(true);
      }
    } catch (e) {
      console.error("Failed to dismiss hint", e);
    }
  };

  const restore = () => {
    try {
      const stored = localStorage.getItem(storageKey);
      const dismissed = stored ? JSON.parse(stored) : [];
      const updated = dismissed.filter((key: unknown) => key !== hintKey);
      localStorage.setItem(storageKey, JSON.stringify(updated));
      setIsDismissed(false);
    } catch (e) {
      console.error("Failed to restore hint", e);
    }
  };

  return { isDismissed, dismiss, restore, isReady };
}

export function resetUserHints(storagePrefix: string, userId: string) {
  const storageKey = `${storagePrefix}${userId}`;
  try {
    localStorage.removeItem(storageKey);
  } catch (e) {
    console.error("Failed to reset user hints", e);
  }
}

type DismissibleHintAlertProps = AlertProps & {
  hintKey: string;
  /** Bereits übersetztes aria-label für das Schließen. Fehlt es, gilt Shared-i18n oder Deutsch. */
  closeAriaLabel?: string;
};

export function DismissibleHintAlert({ hintKey, onClose, closeAriaLabel, ...props }: DismissibleHintAlertProps) {
  const i18n = useI18nOptional();
  const { isDismissed, dismiss } = useDismissibleHint(hintKey);
  const closeLabel = closeAriaLabel ?? i18n?.tr("Close") ?? sharedCommonDe.Close;

  if (isDismissed) {
    return null;
  }

  return (
    <Alert
      {...props}
      style={{ marginBottom: spacing.sectionGap, ...props.style }}
      closable
      closeIcon={<CloseOutlined aria-label={closeLabel} />}
      onClose={(event) => {
        dismiss();
        onClose?.(event);
      }}
    />
  );
}
